import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    Grid,
    Paper,
    Typography,
    Alert,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import {
    Department,
    DepartmentCategory,
    DepartmentVisibility,
    EcommerceEvent,
    EventSeatmap,
    SimpleEventSeatmap,
} from "../../models/Event";
import AuthStore from "../../stores/AuthStore";
import CartStore from "../../stores/CartStore";
import NothingHereYet from "../NothingHereYet";
import CompositeDepartmentListing from "./CompositeDepartmentListing";
import DepartmentListing from "./DepartmentListing";
import Link from "next/link";
import React from "react";
import { EventPackage } from "../../models/AssignedSeating";

type Props = {
    eventList: EcommerceEvent;
    cartStore: CartStore | null;
    showAll?: boolean;
};

const DepartmentList = observer(
    ({ eventList, cartStore, showAll = false }: Props) => {
        const filteredDepartments =
            eventList.event?.departments.filter(
                (it) =>
                    ([
                        DepartmentVisibility.OPEN,
                        DepartmentVisibility.VISIBLE_INACTIVE,
                    ].includes(
                        AuthStore.boxoffice
                            ? it.boxofficeVisibility
                            : it.ecommerceVisibility,
                    ) &&
                        it.from <= Date.now() &&
                        it.to >= Date.now()) ||
                    showAll,
            ) ?? [];

        const filteredSeatmaps =
            eventList.event?.seatmaps.filter(
                (it) =>
                    ([
                        DepartmentVisibility.OPEN,
                        DepartmentVisibility.VISIBLE_INACTIVE,
                    ].includes(
                        AuthStore.boxoffice
                            ? it.boxofficeVisibility
                            : it.ecommerceVisibility,
                    ) &&
                        it.startDate <= Date.now() &&
                        it.endDate >= Date.now()) ||
                    showAll,
            ) ?? [];

        const filteredPackages =
            eventList.event?.packages.filter(
                (it) =>
                    ([
                        DepartmentVisibility.OPEN,
                        DepartmentVisibility.VISIBLE_INACTIVE,
                    ].includes(
                        AuthStore.boxoffice
                            ? it.boxofficeVisibility
                            : it.ecommerceVisibility,
                    ) &&
                        it.startDate <= Date.now() &&
                        it.endDate >= Date.now()) ||
                    showAll,
            ) ?? [];

        const fullList = [
            ...filteredDepartments.map((it) => ({
                ...it,
                weight: it.orderWeight,
                type: "department",
            })),
            ...filteredSeatmaps.map((it) => ({
                ...it,
                type: "seatmap",
            })),
            ...filteredPackages.map((it) => ({
                ...it,
                type: "package",
            })),
        ];

        const groupedByCategory = fullList.reduce(
            (a, b) => {
                if (b.category) {
                    if (!a[b.category.id]) {
                        a[b.category.id] = {
                            category: b.category,
                            items: [b],
                        };
                    } else {
                        a[b.category.id].items.push(b);
                    }
                } else {
                    if (!a[0]) {
                        a[0] = {
                            category: {
                                id: 0,
                                weight: 9999,
                                name: "Event Tickets",
                            },
                            items: [b],
                        };
                    } else {
                        a[0].items.push(b);
                    }
                }
                return a;
            },
            {} as {
                [key: number]: {
                    category: DepartmentCategory;
                    items: typeof fullList;
                };
            },
        );

        // Helper function to check if seatmap is sold out
        const isSeatmapSoldOut = (seatmap: SimpleEventSeatmap): boolean => {
            // You'll need to implement this logic based on your data structure
            // This could check available seats, inventory, or a specific soldOut flag
            return [57, 58, 60].includes(seatmap.id) && !AuthStore.boxoffice;
            // Adjust the condition based on your actual seatmap data structure
        };

        return (
            <Grid container spacing={2}>
                {Object.values(groupedByCategory)
                    .sort((a, b) => a.category.weight - b.category.weight)
                    .map((group) => (
                        <Grid item xs={12} key={group.category.id}>
                            <Typography
                                variant="h5"
                                sx={{ mb: 2, textAlign: "center" }}
                            >
                                {group.category.name}
                            </Typography>
                            <Grid container spacing={1}>
                                {group.items
                                    .sort(
                                        (a, b) =>
                                            (a.weight ?? 999) -
                                            (b.weight ?? 999),
                                    )
                                    .map((item) => (
                                        <Grid
                                            item
                                            xs={12}
                                            key={`${item.type}-${item.id}`}
                                        >
                                            {item.type === "department" && (
                                                <DepartmentListing
                                                    dept={item as Department}
                                                    cartUrl={
                                                        eventList.cartUrl ?? ""
                                                    }
                                                    eventId={
                                                        eventList.event?.id ?? 0
                                                    }
                                                    onAddToCart={(
                                                        comp: boolean,
                                                    ) => {
                                                        cartStore?.incrementGaQuantity(
                                                            item.id,
                                                            comp,
                                                            item as Department,
                                                        );
                                                    }}
                                                    emsClientIds={
                                                        eventList.event?.client
                                                            .equivalentEMS2ClientIds ??
                                                        []
                                                    }
                                                    clientId={
                                                        eventList.event?.client
                                                            .id ?? -1
                                                    }
                                                    showAll={showAll}
                                                />
                                            )}
                                            {item.type === "seatmap" && (
                                                <Box sx={{ height: "100%" }}>
                                                    <Card
                                                        sx={{ height: "100%" }}
                                                    >
                                                        <CardContent
                                                            sx={{
                                                                height: "100%",
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        "flex",
                                                                    flexDirection:
                                                                        "column",
                                                                    minHeight:
                                                                        "100px",
                                                                    height: "100%",
                                                                    justifyContent:
                                                                        "space-between",
                                                                }}
                                                            >
                                                                <Box>
                                                                    <Box
                                                                        sx={{
                                                                            display:
                                                                                "flex",
                                                                            justifyContent:
                                                                                "space-between",
                                                                            alignItems:
                                                                                "center",
                                                                        }}
                                                                    >
                                                                        <Box>
                                                                            <Typography
                                                                                variant="h6"
                                                                                gutterBottom
                                                                            >
                                                                                {
                                                                                    (
                                                                                        item as SimpleEventSeatmap
                                                                                    )
                                                                                        .name
                                                                                }
                                                                            </Typography>
                                                                        </Box>
                                                                    </Box>
                                                                    <Divider
                                                                        sx={{
                                                                            my: 1,
                                                                        }}
                                                                    />
                                                                    <Box
                                                                        component="div"
                                                                        dangerouslySetInnerHTML={{
                                                                            __html:
                                                                                (
                                                                                    item as SimpleEventSeatmap
                                                                                )
                                                                                    .description ??
                                                                                "",
                                                                        }}
                                                                    />

                                                                    {/* Sold Out Alert */}
                                                                    {isSeatmapSoldOut(
                                                                        item as SimpleEventSeatmap,
                                                                    ) && (
                                                                        <Alert
                                                                            severity="warning"
                                                                            sx={{
                                                                                mt: 2,
                                                                            }}
                                                                        >
                                                                            <Typography variant="body2">
                                                                                <strong>
                                                                                    Sold
                                                                                    Out
                                                                                    Online
                                                                                </strong>
                                                                                <br />
                                                                                General
                                                                                Admission
                                                                                Standing
                                                                                tickets
                                                                                are
                                                                                still
                                                                                available
                                                                                for
                                                                                purchase
                                                                                in-person
                                                                                at
                                                                                the
                                                                                Grandstand
                                                                                Box-Office.
                                                                            </Typography>
                                                                        </Alert>
                                                                    )}
                                                                </Box>
                                                                <Box
                                                                    sx={{
                                                                        display:
                                                                            "flex",
                                                                        justifyContent:
                                                                            "flex-end",
                                                                        mt: 2,
                                                                    }}
                                                                >
                                                                    <Link
                                                                        href={`/store/${eventList.cartUrl}/${eventList.event?.id}/seatmap/${item.id}`}
                                                                        passHref
                                                                    >
                                                                        <Button
                                                                            variant="contained"
                                                                            size="small"
                                                                        >
                                                                            Buy
                                                                            Tickets
                                                                        </Button>
                                                                    </Link>
                                                                </Box>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </Box>
                                            )}
                                            {item.type === "package" && (
                                                <Card>
                                                    <CardContent>
                                                        <Box
                                                            sx={{
                                                                display: "flex",
                                                                flexDirection: {
                                                                    xs: "column",
                                                                    md: "row",
                                                                },
                                                                alignItems:
                                                                    "center",
                                                                justifyContent:
                                                                    "space-between",
                                                                gap: "20px",
                                                            }}
                                                        >
                                                            <Box>
                                                                <Typography variant="h6">
                                                                    {
                                                                        (
                                                                            item as EventPackage
                                                                        ).name
                                                                    }
                                                                </Typography>
                                                                <Box
                                                                    component="div"
                                                                    dangerouslySetInnerHTML={{
                                                                        __html:
                                                                            (
                                                                                item as EventPackage
                                                                            )
                                                                                .description ??
                                                                            "",
                                                                    }}
                                                                ></Box>
                                                            </Box>
                                                            <Link
                                                                href={`/store/${eventList.cartUrl}/${eventList.event?.id}/seatmap-package/${item.id}`}
                                                            >
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    sx={{
                                                                        mt: {
                                                                            xs: 2,
                                                                            md: 0,
                                                                        },
                                                                        width: "120px",
                                                                    }}
                                                                    fullWidth
                                                                >
                                                                    View Package
                                                                </Button>
                                                            </Link>
                                                        </Box>
                                                    </CardContent>
                                                </Card>
                                            )}
                                        </Grid>
                                    ))}
                            </Grid>
                        </Grid>
                    ))}
                {!fullList.length && (
                    <Grid item xs={12}>
                        <NothingHereYet message="No tickets currently on sale." />
                    </Grid>
                )}
            </Grid>
        );
    },
);

export default DepartmentList;
